export const MAX_BEHAVIORS = 3;
export const MIN_BEHAVIORS = 1;

export type BehaviorChangeStep =
  | 'intro'
  | 'identify'
  | 'behavior'
  | 'beliefs'
  | 'thoughts'
  | 'results';

export const BEHAVIOR_CHANGE_STEPS: { id: BehaviorChangeStep; label: string }[] = [
  { id: 'intro', label: 'Introduction' },
  { id: 'identify', label: 'Identify patterns' },
  { id: 'behavior', label: 'Behavior' },
  { id: 'beliefs', label: 'Beliefs & fears' },
  { id: 'thoughts', label: 'Thoughts' },
  { id: 'results', label: 'Your worksheet' },
];

export interface BehaviorPattern {
  id: string;
  label: string;
  /** Q1 — when I act it out, what am I willing / unwilling to do? */
  willingUnwillingCurrent: string;
  /** Q2 — what must I be willing / unwilling to do to change? */
  willingUnwillingToChange: string;
  /** Q3 — what has this pattern made me believe? */
  beliefsHeld: string;
  /** Q4 — what must I be willing to believe instead? */
  beliefsNeeded: string;
  /** Simplified framework — fears driving the behavior */
  fearsDriving: string;
  /** Simplified framework — what to do to overcome those fears */
  overcomeFears: string;
  /** Q5 — what have I been thinking? */
  currentThinking: string;
  /** Q6 — what thinking must change? */
  thinkingToChange: string;
}

export interface BehaviorChangeState {
  behaviors: BehaviorPattern[];
  completedAt?: string;
}

export function createBehavior(): BehaviorPattern {
  return {
    id: crypto.randomUUID(),
    label: '',
    willingUnwillingCurrent: '',
    willingUnwillingToChange: '',
    beliefsHeld: '',
    beliefsNeeded: '',
    fearsDriving: '',
    overcomeFears: '',
    currentThinking: '',
    thinkingToChange: '',
  };
}

export function stepIndex(step: BehaviorChangeStep): number {
  return BEHAVIOR_CHANGE_STEPS.findIndex(s => s.id === step);
}

export function nextStep(step: BehaviorChangeStep): BehaviorChangeStep | null {
  const idx = stepIndex(step);
  if (idx < 0 || idx >= BEHAVIOR_CHANGE_STEPS.length - 1) return null;
  return BEHAVIOR_CHANGE_STEPS[idx + 1]!.id;
}

export function prevStep(step: BehaviorChangeStep): BehaviorChangeStep | null {
  const idx = stepIndex(step);
  if (idx <= 0) return null;
  return BEHAVIOR_CHANGE_STEPS[idx - 1]!.id;
}

function hasText(value: string): boolean {
  return value.trim().length > 0;
}

export function canAdvance(step: BehaviorChangeStep, state: BehaviorChangeState): boolean {
  switch (step) {
    case 'intro':
      return true;
    case 'identify':
      return (
        state.behaviors.length >= MIN_BEHAVIORS &&
        state.behaviors.every(b => hasText(b.label))
      );
    case 'behavior':
      return state.behaviors.every(
        b => hasText(b.willingUnwillingCurrent) && hasText(b.willingUnwillingToChange),
      );
    case 'beliefs':
      return state.behaviors.every(
        b =>
          hasText(b.beliefsHeld) &&
          hasText(b.beliefsNeeded) &&
          hasText(b.fearsDriving) &&
          hasText(b.overcomeFears),
      );
    case 'thoughts':
      return state.behaviors.every(
        b => hasText(b.currentThinking) && hasText(b.thinkingToChange),
      );
    case 'results':
      return true;
    default:
      return false;
  }
}
